import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    List,
    ListItem,
    ListItemText,
    Switch,
    Button,
    Collapse,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Stack,
    Chip,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PiletItem } from '../internals/types/piletItem';
import { account } from '../adal';

// Group pilets by their name to handle multiple versions
function groupPiletsByName(items: PiletItem[]) {
    const groupedItems: Record<string, PiletItem[]> = {};
    
    items.forEach((item) => {
        // Use name as the key for grouping
        const group = item.name;
        if (!groupedItems[group]) {
            groupedItems[group] = [];
        }
        groupedItems[group].push(item);
    });
    
    // Sort each group by version (descending)
    Object.keys(groupedItems).forEach(group => {
        groupedItems[group].sort((a, b) => {
            return (b.version || '0.0.0').localeCompare(a.version || '0.0.0', undefined, { numeric: true });
        });
    });
    
    return groupedItems;
}

function isAdmin() {
  const roles = account()?.idTokenClaims?.roles || [];
  return roles.includes('it-admin');
}

export default function Settings() {
    const [enabledStates, setEnabledStates] = useState<Record<string, boolean>>({});
    
    // State for pilet data
    const [piletData, setPiletData] = useState<Record<string, PiletItem[]>>({});
    const [loading, setLoading] = useState(true);
    const [expandedPilets, setExpandedPilets] = useState<Record<string, boolean>>({});
    const [activePiletVersions, setActivePiletVersions] = useState<Record<string, string>>({});
    
    // State for tracking which pilets are currently being toggled (for loader)
    const [togglingPilets, setTogglingPilets] = useState<Record<string, boolean>>({});
    
    // Alert state for notifications
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
    
    
    // Toggle pilet enabled/disabled state
    const togglePilet = async (name: string) => {
        console.log(`Toggling pilet: ${name}`);
        const currentEnabled = enabledStates[name] || false;
        const version = activePiletVersions[name] || '0.0.0';
        const action = currentEnabled ? 'disable' : 'enable';
        
        // Set toggling state to show loader
        setTogglingPilets(prev => ({ ...prev, [name]: true }));
        
        try {
            // Make API call to enable/disable the pilet
            const response = await fetch(`https://cannon-pilet-feed-c0hkbmf3e8hzdsfm.centralus-01.azurewebsites.net/api/pilet/${name}/${version}/${action}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to ${action} pilet: ${response.statusText}`);
            }
            
            // After successful API call, refresh the data to get updated state
            // This is the most reliable way to ensure our UI reflects the server state
            const refreshResponse = await fetch('https://cannon-pilet-feed-c0hkbmf3e8hzdsfm.centralus-01.azurewebsites.net/api/feed/all');
            if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                const refreshedFeed: PiletItem[] = refreshData.items || [];
                const refreshedGrouped = groupPiletsByName(refreshedFeed);
                
                // Update our state with the refreshed data
                setPiletData(refreshedGrouped);
                
                // Update enabled states and active versions based on refreshed data
                const refreshedStates: Record<string, boolean> = {};
                const refreshedActiveVersions: Record<string, string> = {};
                
                Object.keys(refreshedGrouped).forEach(piletName => {
                    if (refreshedGrouped[piletName].length > 0) {
                        // Find the active version from the feed data
                        const activeVersionPilet = refreshedGrouped[piletName].find(pilet => 
                            pilet.custom && typeof pilet.custom.enabled === 'boolean' && pilet.custom.enabled
                        ) || refreshedGrouped[piletName][0];
                        
                        // Store the active version
                        refreshedActiveVersions[piletName] = activeVersionPilet.version || '0.0.0';
                        
                        // Check if ANY version of this pilet is enabled
                        // A pilet is considered enabled if at least one of its versions is enabled
                        refreshedStates[piletName] = refreshedGrouped[piletName].some(pilet => 
                            pilet.custom && typeof pilet.custom.enabled === 'boolean' && pilet.custom.enabled
                        );
                    }
                });
                
                setEnabledStates(refreshedStates);
                setActivePiletVersions(refreshedActiveVersions);
            } else {
                // If refresh fails, just toggle the local state
                setEnabledStates(prev => ({ ...prev, [name]: !currentEnabled }));
            }
            
            // Show success notification
            setAlertMessage(`Successfully ${action}d pilet: ${name}`);
            setAlertSeverity('success');
            setAlertOpen(true);
            
        } catch (error) {
            console.error(`Error ${action}ing pilet:`, error);
            // Show error notification
            setAlertMessage(`Failed to ${action} pilet: ${name}`);
            setAlertSeverity('error');
            setAlertOpen(true);
        } finally {
            // Clear toggling state
            setTogglingPilets(prev => ({ ...prev, [name]: false }));
        }
    };
    
    // Toggle expanded state for version details
    const toggleExpand = (name: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedPilets(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };
    
    // Handle version change for pilet
    const handleVersionChange = (piletName: string, version: string) => {
        console.log(`Changing ${piletName} to version ${version}`);
        setActivePiletVersions(prev => ({
            ...prev,
            [piletName]: version
        }));
    };
    
    // Apply the selected version change
    const applyVersionChange = async (piletName: string) => {
        const version = activePiletVersions[piletName];
        if (!version) return;
        
        try {
            // Set a loader state for this operation if needed
            setTogglingPilets(prev => ({ ...prev, [piletName]: true }));
            
            // Make API call to set the active version
            const response = await fetch(`https://cannon-pilet-feed-c0hkbmf3e8hzdsfm.centralus-01.azurewebsites.net/api/pilet/${piletName}/${version}/enable`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic 02666ad3f5bd4a41f61d50078402dc65665b2b41a4971c7d651e82bb21d68b91` // Use your auth token here
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to set active version: ${response.statusText}`);
            }
            
            // Refresh data to get updated state
            const refreshResponse = await fetch('https://cannon-pilet-feed-c0hkbmf3e8hzdsfm.centralus-01.azurewebsites.net/api/feed/all');
            if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                const refreshedFeed: PiletItem[] = refreshData.items || [];
                const refreshedGrouped = groupPiletsByName(refreshedFeed);
                
                // Update our state with the refreshed data
                setPiletData(refreshedGrouped);
                
                // Update enabled states and active versions based on refreshed data
                const refreshedActiveVersions: Record<string, string> = {};
                
                Object.keys(refreshedGrouped).forEach(name => {
                    if (refreshedGrouped[name].length > 0) {
                        // Find the active version from the feed data
                        const activeVersionPilet = refreshedGrouped[name].find(pilet => 
                            pilet.custom && typeof pilet.custom.enabled === 'boolean' && pilet.custom.enabled
                        ) || refreshedGrouped[name][0];
                        
                        // Store the active version
                        refreshedActiveVersions[name] = activeVersionPilet.version || '0.0.0';
                    }
                });
                
                setActivePiletVersions(refreshedActiveVersions);
            }
            
            // Show success notification
            setAlertMessage(`Successfully enabled version ${version} for ${piletName}`);
            setAlertSeverity('success');
            setAlertOpen(true);
            
        } catch (error) {
            console.error(`Error activating version:`, error);
            // Show error notification
            setAlertMessage(`Failed to enable version ${version} for ${piletName}`);
            setAlertSeverity('error');
            setAlertOpen(true);
        } finally {
            // Clear any loader state
            setTogglingPilets(prev => ({ ...prev, [piletName]: false }));
        }
    };
    
    // Handle closing the alert
    const handleAlertClose = () => {
        setAlertOpen(false);
    };
    
    // Fetch pilet data from feed
    useEffect(() => {
        const fetchPiletData = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://cannon-pilet-feed-c0hkbmf3e8hzdsfm.centralus-01.azurewebsites.net/api/feed/all');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                const feed: PiletItem[] = data.items || [];
                const grouped = groupPiletsByName(feed);
                setPiletData(grouped);
                console.log('Fetched pilet data:', grouped);
                
                // Initialize active versions with the latest version of each pilet
                const activeVersions: Record<string, string> = {};
                
                // Initialize enabled states based on API data
                const initialEnabledStates: Record<string, boolean> = {};
                
                Object.keys(grouped).forEach(name => {
                    if (grouped[name].length > 0) {
                        // Find the active version by checking for the version that's enabled in custom property
                        // If no version is marked as enabled, use the first (latest) version
                        const activeVersionPilet = grouped[name].find(pilet => 
                            pilet.custom && typeof pilet.custom.enabled === 'boolean' && pilet.custom.enabled
                        ) || grouped[name][0];
                        
                        // Set active version based on the enabled version from the feed
                        activeVersions[name] = activeVersionPilet.version || '0.0.0';
                        
                        // Check if ANY version of this pilet is enabled
                        // A pilet is considered enabled if at least one of its versions is enabled
                        const isEnabled = grouped[name].some(pilet => 
                            pilet.custom && typeof pilet.custom.enabled === 'boolean' && pilet.custom.enabled
                        );
                        
                        initialEnabledStates[name] = isEnabled;
                        
                        console.log(`Pilet "${name}" enabled status:`, initialEnabledStates[name], 
                                   'based on any version being enabled',
                                   'active version:', activeVersions[name]);
                    }
                });
                
                console.log('Active pilet versions:', activeVersions);
                console.log('Initial enabled states:', initialEnabledStates);
                
                setActivePiletVersions(activeVersions);
                setEnabledStates(initialEnabledStates);
            } catch (error) {
                console.error('Failed to fetch pilet data:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchPiletData();
    }, []);
    
    return (
        isAdmin() && (
        <Box sx={{ p: 2, width: '100%' }}>
            <Typography variant="h1" gutterBottom>
                Settings Page
            </Typography>

            <Card sx={{ mb: 2 }}>
                <CardHeader 
                    title="Pilet Management" 
                    subheader="Enable or disable pilets and manage versions" 
                />
                <CardContent>
                    {loading ? (
                        <Typography>Loading pilet data...</Typography>
                    ) : (
                        <List>
                            {/* Use piletData which contains all pilets, not just enabled ones */}
                            {Object.keys(piletData).map((piletName) => {
                                const displayName = piletName;
                                const isExpanded = expandedPilets[piletName] || false;
                                const piletVersions = piletData[piletName] || [];
                                const activeVersion = activePiletVersions[piletName] || 
                                    (piletVersions.length > 0 ? piletVersions[0].version : 'Unknown');
                                const isEnabled = enabledStates[piletName] || false;
                                
                                return (
                                    <React.Fragment key={piletName}>
                                        <ListItem
                                            disablePadding
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                cursor: togglingPilets[piletName] ? 'default' : 'pointer',
                                                transition: 'background 0.2s',
                                                p: 1,
                                                '&:hover': {
                                                    backgroundColor: togglingPilets[piletName] ? 'transparent' : 'action.hover',
                                                },
                                                opacity: togglingPilets[piletName] ? 0.7 : 1,
                                            }}
                                            onClick={() => !togglingPilets[piletName] && togglePilet(piletName)}
                                        >
                                            <ListItemText 
                                                primary={displayName} 
                                                secondaryTypographyProps={{
                                                    component: 'div' // Change from p to div to allow nesting
                                                }}
                                                secondary={
                                                    <>
                                                        Current version:{' '}
                                                        <Chip 
                                                            size="small" 
                                                            label={activeVersion} 
                                                            color="primary" 
                                                            variant="outlined" 
                                                        />
                                                    </>
                                                }
                                            />
                                            
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {togglingPilets[piletName] ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Switch
                                                        color="primary"
                                                        sx={{
                                                            '& .MuiSwitch-thumb': {
                                                                transform: 'translateY(7px)',
                                                            },
                                                            '& .MuiSwitch-track': { backgroundColor: 'grey' }
                                                        }}
                                                        checked={isEnabled}
                                                        onClick={(event) => event.stopPropagation()}
                                                        onChange={() => togglePilet(piletName)}
                                                        disabled={togglingPilets[piletName]}
                                                    />
                                                )}
                                                
                                                {piletVersions.length > 1 && (
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => toggleExpand(piletName, e)}
                                                    >
                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </ListItem>
                                        
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Available Versions
                                                </Typography>
                                                
                                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                    <InputLabel>Select Version</InputLabel>
                                                    <Select
                                                        value={activePiletVersions[displayName] || ''}
                                                        label="Select Version"
                                                        onChange={(e: SelectChangeEvent) => 
                                                            handleVersionChange(displayName, e.target.value)
                                                        }
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {piletVersions.map((version) => {
                                                            // Check if this is the active version from the feed
                                                            const isActiveVersion = version.version === activeVersion;
                                                            // Check if this version is enabled in custom property
                                                            const isEnabled = version.custom && 
                                                                typeof version.custom.enabled === 'boolean' && 
                                                                version.custom.enabled;
                                                            
                                                            return (
                                                                <MenuItem 
                                                                    key={version.version} 
                                                                    value={version.version}
                                                                    sx={{
                                                                        fontWeight: isActiveVersion ? 'bold' : 'normal',
                                                                        backgroundColor: isActiveVersion ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                                                    }}
                                                                >
                                                                    {version.version}
                                                                    {isActiveVersion && (
                                                                        <Chip 
                                                                            size="small" 
                                                                            label="Active" 
                                                                            color="success" 
                                                                            variant="outlined"
                                                                            sx={{ ml: 1, height: 20 }}
                                                                        />
                                                                    )}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </Select>
                                                </FormControl>
                                                
                                                <Button 
                                                    variant="outlined" 
                                                    color="primary" 
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        applyVersionChange(displayName);
                                                    }}
                                                    disabled={togglingPilets[displayName]}
                                                >
                                                    {togglingPilets[displayName] ? (
                                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                                    ) : null}
                                                    Apply Version
                                                </Button>
                                            </Box>
                                        </Collapse>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </CardContent>
            </Card>

            {/* <Card>
                <CardHeader title="Feature Flags" subheader="Manage feature flags for your application" />
                <CardContent>
                    <List>
                        {featureFlags.map((flag) => (
                            <ListItem disablePadding key={flag}>
                                <ListItemText primary={flag} />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card> */}

            {/* Notification alert */}
            <Snackbar 
                open={alertOpen} 
                autoHideDuration={6000} 
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleAlertClose} 
                    severity={alertSeverity} 
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
        )
    );
}